// generateSearchResultsPageJsonLd.js
'use strict';

import { getHreflang } from '../resource/langPath.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { searchPageTDKTranslateKeys } from '../resource/searchConstants.js';

/**
 * Generate SearchResultsPage JSON-LD for search pages
 * @param {Object} params
 * @param {string} params.query - Search query string
 * @param {string} params.type - Search type (all, flix, swagger, story, hashtag)
 * @param {string} params.url - Full URL of the search page
 * @param {Object} params.i18n - i18n instance (required)
 * @returns {Array} Array of JSON-LD objects [WebPage, BreadcrumbList]
 */
const generateSearchResultsPageJsonLd = async ({
  query = '',
  type = 'all',
  url = '',
  i18n,
}) => {
  const currentLanguage = i18n.language;
  const normalizedInLanguage = getHreflang(currentLanguage);

  const loadLanguagePromise = async () => {
    if (!i18n.hasResourceBundle(currentLanguage, TranslationNamespace.SEARCH)) {
      await i18n.reloadResources(currentLanguage, TranslationNamespace.SEARCH);
    }
  };
  await loadLanguagePromise();

  // Get TDK translation keys based on search type
  const tdkKeys =
    searchPageTDKTranslateKeys[type] || searchPageTDKTranslateKeys.default;

  // Generate title and description for a specific language
  const getTitleByLang = lang => {
    const baseTitle = i18n.t(tdkKeys.title, {
      lng: lang,
      ns: TranslationNamespace.SEARCH,
    });
    return query ? `${query} - ${baseTitle}` : baseTitle;
  };

  const getDescriptionByLang = lang => {
    const baseDescription = i18n.t(tdkKeys.description, {
      lng: lang,
      ns: TranslationNamespace.SEARCH,
    });
    return query ? `${query} - ${baseDescription}` : baseDescription;
  };

  // Base WebPage schema
  const webPage = {
    '@type': 'WebPage',
    '@id': `${url}#webpage`,
    url,
    inLanguage: normalizedInLanguage,
    name: getTitleByLang(currentLanguage),
    description: getDescriptionByLang(currentLanguage),
    breadcrumb: {
      '@id': `${url}#breadcrumb`,
    },
  };

  // BreadcrumbList schema
  const breadcrumbList = {
    '@type': 'BreadcrumbList',
    '@id': `${url}#breadcrumb`,
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: i18n.t('seo-home-title', {
          lng: currentLanguage,
          ns: TranslationNamespace.SEO,
        }),
        item: {
          '@id': 'https://swag.live/#website',
        },
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: getTitleByLang(currentLanguage),
        item: {
          '@id': `${url}#webpage`,
        },
      },
    ],
  };

  // Return array format (not wrapped in @context/@graph)
  return [webPage, breadcrumbList];
};

export default generateSearchResultsPageJsonLd;
